import React, { useEffect, useState } from 'react'
import { Container, Div } from './SelectedModel.styles'
import { useId, Input, Label, Tooltip } from '@fluentui/react-components'
import Button from '../Button/Button'
import { Product } from '../../services/types/product'
import useDebounce from '../../hooks/useDebounce'

type SelectedModelProps = {
  active: boolean
  product: Product
  onSelectionChange: (productId: string, selected: boolean) => void
  selectedProducts: Product[]
  setSelectedProducts: (products: Product[]) => void
}

const SelectedModel: React.FC<SelectedModelProps> = ({
  active,
  product,
  onSelectionChange,
  selectedProducts,
  setSelectedProducts
}) => {
  const {
    partName,
    productNumber,
    FTLQuantity,
    units: prevUnits,
    leadTime
  } = product

  const inputId = useId('input')
  const [units, setUnits] = React.useState(
    prevUnits ? prevUnits.toString() : ''
  )
  const [showTooltip, setShowTooltip] = useState(false)
  const debouncedUnits = useDebounce<number>(
    units ? parseInt(units, 10) : 0,
    500
  )

  const handleClick = () => {
    onSelectionChange(product.id, false)
  }

  const handleRemoveClick = () => {
    const updatedProducts = selectedProducts.filter(
      (p: Product) => p.id !== product.id
    )
    setSelectedProducts(updatedProducts)
  }

  const pointValue = 100 / FTLQuantity
  const totalPoints = debouncedUnits ? debouncedUnits * pointValue : 0

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const numericValue = inputValue.replace(/[^0-9]/g, '')

    // Show the tooltip if the input contains non-numeric characters
    if (numericValue !== inputValue) {
      setShowTooltip(true)
      // Automatically hide the tooltip after 500ms
      setTimeout(() => {
        setShowTooltip(false)
      }, 1200)
    } else {
      setShowTooltip(false)
    }

    setUnits(numericValue)
  }

  useEffect(() => {
    const updatedProducts = selectedProducts.map((p: Product) =>
      p.id === product.id ? { ...p, units: debouncedUnits } : p
    )
    setSelectedProducts(updatedProducts)
  }, [debouncedUnits, product.id])

  return (
    <Container $active={active} onClick={handleClick}>
      <Div>
        {productNumber && (
          <p>
            Model #: <span>{productNumber}</span>
          </p>
        )}
        {partName && (
          <p>
            <span>{partName}</span>
          </p>
        )}
        {leadTime && (
          <p>
            <span>{leadTime ? leadTime : 'Loading...'}</span>
          </p>
        )}
      </Div>
      <Div>
        <p data-label="FTL Qty:">{FTLQuantity}</p>
      </Div>
      <Div>
        <Label
          htmlFor={inputId}
          size="large"
          style={{ paddingInlineEnd: '12px' }}
        >
          Units
        </Label>
        <Tooltip
          content="Only numbers are allowed."
          relationship="label"
          visible={showTooltip}
          appearance="inverted"
          withArrow
          hideDelay={500}
        >
          <Input
            id={inputId}
            size="large"
            onChange={handleInputChange}
            value={units}
            autoComplete="off"
          />
        </Tooltip>
      </Div>
      <Div>
        <div>
          <p data-label="Points:">
            {debouncedUnits === 0
              ? 0
              : parseFloat(String(totalPoints)).toFixed(1)}
          </p>
        </div>
      </Div>
      <Div>
        <Button
          onClick={handleRemoveClick}
          text="Remove"
          size="large"
          appearance="primary"
        />
      </Div>
    </Container>
  )
}

export default SelectedModel
